/**
 * Service check-in wizard.
 *
 * Enhancement only. snippets/rifugio-service-checkin.liquid is a single native
 * <form action="/cart/add"> whose steps are ordinary fieldsets. The markup ships
 * the form, steps 2-5 and the submit button hidden by `.rf-checkin__preboot` so
 * that nothing flashes before this file runs, and a <noscript><style> block in
 * that snippet turns that hide off again — so with scripting off the customer
 * gets one long form that still submits. Nothing here fetches, and there is no
 * client-side cart state to fall out of sync.
 *
 * What it adds: one step at a time, per-step validation, a review read-back,
 * deposit selection, and optional dictation.
 *
 * WHY A CUSTOM ELEMENT. This was a top-level `document.querySelector` in a
 * module. Module bodies execute once per URL, so re-rendering the section in
 * the theme editor left markup with nothing bound to it — the failure
 * snippets/inline-scripts.liquid exists in order to describe. An upgrade runs
 * per instance, so every re-render gets a live wizard for free.
 */

import { initServiceFilter } from './rifugio-service-filter.js';

/* Draft persistence. Versioned key: the shape below is read straight back into
   form controls, so a future change to what is stored must not meet an old
   payload — bump the version and old drafts are simply ignored.

   Namespaced by location as well as version. The two shops keep separate work
   in progress: without the suffix a bike half-checked-in at Darling Hill came
   back as "Resume your check-in" on the Litchfield Hills page, restoring
   answers about a bike in another state — and the checked service boxes refer
   to a menu that is rendered per location, so the restored selection was not
   even meaningful there. The location comes from data-location on the element
   (set in the Liquid from template.suffix), never from the URL: the wizard is
   a custom element that has to keep working wherever it is mounted, including
   a theme-editor re-render. */
const DRAFT_KEY_BASE = 'rifugio:checkin-draft:v1';
const draftKeyFor = (location) => `${DRAFT_KEY_BASE}:${location || 'unknown'}`;

/* One-time sweep of the pre-namespace key. Those payloads are unreachable now
   — nothing reads DRAFT_KEY_BASE bare any more — so without this they sit in
   localStorage forever: the TTL only ever runs on a key something reads.
   Deleted rather than migrated on purpose. A legacy draft carries no record of
   which shop it was started at, so adopting it here would have to guess, and
   guessing wrong reintroduces exactly the bleed this change removes. */
const purgeLegacyDraft = () => {
  try {
    window.localStorage.removeItem(DRAFT_KEY_BASE);
  } catch {
    /* private mode — nothing to purge, and nothing that can go wrong */
  }
};

/* A half-finished check-in is worth keeping for a few days, not indefinitely.
   Past this the bike has usually been dealt with, and repopulating someone's
   name and symptoms weeks later is a surprise rather than a convenience. */
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/* How long the "we kept your check-in" line stays up. Long enough to read the
   sentence twice; short enough that it is gone before the customer starts
   typing. The photo variant is exempt — see hideDraftNotice(). */
const DRAFT_NOTICE_MS = 8000;
const DRAFT_NOTICE_FADE_MS = 300;

/* localStorage throws in Safari private mode and when a quota is exhausted.
   A draft is a convenience; nothing here may take the wizard down with it. */
const draftStore = {
  read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (!data.savedAt || Date.now() - data.savedAt > DRAFT_TTL_MS) {
        this.clear(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  },
  write(key, data) {
    try {
      window.localStorage.setItem(key, JSON.stringify(data));
    } catch {
      /* Quota or private mode — the form keeps working, it just will not resume. */
    }
  },
  clear(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* nothing to do */
    }
  },
};

/* "3 minutes ago" / "on Tuesday" — enough for the customer to recognise the
   draft as theirs without printing a timestamp at them. */
const describeAge = (savedAt) => {
  const mins = Math.round((Date.now() - savedAt) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `on ${new Date(savedAt).toLocaleDateString(undefined, { weekday: 'long' })}`;
};

class RifugioServiceCheckin extends HTMLElement {
  connectedCallback() {
    // connectedCallback fires again if the element is ever moved in the DOM.
    // Binding twice would double every listener.
    if (this.ready) return;

    const form = this.querySelector('[data-checkin-form]');
    const intro = this.querySelector('[data-stage="intro"]');
    const startBtn = this.querySelector('[data-checkin-start]');
    if (!form || !intro || !startBtn) return;

    this.ready = true;

    /* Resolved before any read or write below. Missing data-location falls back
       to the 'unknown' namespace rather than the shared one — a draft that
       cannot say where it came from is kept away from both shops. */
    this.draftKey = draftKeyFor(this.dataset.location);
    purgeLegacyDraft();
    this.form = form;
    this.intro = intro;
    this.steps = [...form.querySelectorAll('[data-step]')];
    this.progress = form.querySelector('[data-checkin-progress]');
    this.backBtn = form.querySelector('[data-checkin-back]');
    this.nextBtn = form.querySelector('[data-checkin-next]');
    this.submitBtn = form.querySelector('[data-checkin-submit]');
    this.variantInput = form.querySelector('[data-checkin-variant]');
    this.reviewList = form.querySelector('[data-checkin-review]');
    this.dueLine = form.querySelector('[data-checkin-due]');

    this.LAST = this.steps.length; // the last step is the review
    this.current = 1;

    /**
     * Take the initial hide off CSS and put it on `hidden`, which is what the
     * rest of this file toggles. The markup cannot ship `hidden` here: Tailwind
     * Preflight emits
     * `[hidden]:where(:not([hidden=until-found])){display:none!important}` into
     * `@layer base`, and CSS Cascade 5 reverses layer order for important
     * declarations — so that layered !important beats an unlayered author
     * !important no matter its specificity, and the <noscript> fallback in the
     * snippet is unlayered. A class avoids the question entirely.
     * Class first, `hidden` second — this all happens
     * inside one task, so no frame is painted between the two.
     */
    this.querySelectorAll('.rf-checkin__preboot').forEach((el) => {
      el.hidden = true;
      el.classList.remove('rf-checkin__preboot');
    });

    /**
     * The browser would otherwise refuse to submit because of `required` fields
     * sitting inside hidden steps — it cannot focus what it cannot show, and
     * Chrome logs "An invalid form control is not focusable". We gate each step
     * manually instead, so by the time submit fires every step has been checked.
     * The `required` attributes stay in the markup because they are what makes
     * the no-JS fallback correct.
     */
    form.noValidate = true;

    startBtn.addEventListener('click', () => {
      this.intro.hidden = true;
      this.form.hidden = false;
      /* Resume where the draft left off. draftStep is null when there is no
         draft, so this is show(1) for everyone else. */
      this.show(this.draftStep || 1);

      /* The notice is on screen from this moment, so time it from here. */
      if (this.draftNoticeTransient) {
        const notice = this.querySelector('[data-checkin-draft]');
        if (notice && !notice.hidden) this.hideDraftNotice(notice);
      }
    });

    this.nextBtn.addEventListener('click', () => {
      if (this.stepIsValid()) this.show(this.current + 1);
    });

    this.backBtn.addEventListener('click', () => this.show(this.current - 1));

    this.menuError = form.querySelector('[data-checkin-menu-error]');

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-service-tier]')) return;
      this.syncDeposit();
      // stepIsValid() is the only other thing that touches this error, and it
      // runs on Next alone. Without clearing it here, a customer who trips the
      // error, ticks a box, then moves with Back or the keyboard is still
      // reading red text about a problem they have already fixed.
      if (this.menuError) this.menuError.hidden = true;
    });

    this.submitting = false;
    this.disabledFields = [];
    this.submitLabel = this.submitBtn.textContent;

    form.addEventListener('submit', (e) => {
      // Implicit submission (Enter in any text field) fires a click at the form's
      // default button — and `hidden` does not remove a submit button from that
      // role. Without this, Enter on step 1 posts a work order containing only a
      // name and charges the $25 default variant. The wizard may only submit from
      // the review. First statement in the handler, so a stray submit mutates no
      // state on its way out.
      if (this.current !== this.LAST) { e.preventDefault(); return; }

      // A multipart POST carrying up to three photos is slow on a phone. Without
      // this, a second tap puts a second diagnostic in the cart.
      if (this.submitting) {
        e.preventDefault();
        return;
      }
      this.submitting = true;
      /* The work order is on its way to the cart, so the draft has done its job.
         Cleared here rather than on the cart page because this POST navigates
         away and there is no reliable callback on the other side. */
      this.clearDraft();
      this.dropEmptyProperties();
      // Deferred: disabling the submitter from inside its own submit event is
      // not reliably safe across browsers. The flag above is what actually
      // stops the double submit; this is only the visible state.
      // Re-check the flag inside the frame: a bfcache restore() can land between
      // the submit and the callback, and an unguarded frame would then re-lock a
      // button restore() had just freed.
      requestAnimationFrame(() => {
        if (!this.submitting) return;
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Checking in…';
      });
    });

    // Safari and Firefox restore this page from bfcache on Back with the DOM
    // exactly as it was left — which, without this, hands the customer a form
    // whose optional fields are disabled and whose submit button is dead.
    this.onPageShow = (e) => { if (e.persisted) this.restore(); };
    window.addEventListener('pageshow', this.onPageShow);

    /* Debounced: Symptoms is a textarea and every keystroke fires input. 400ms
       is short enough that closing the tab mid-sentence keeps the sentence. */
    let draftTimer = null;
    const queueSave = () => {
      window.clearTimeout(draftTimer);
      draftTimer = window.setTimeout(() => this.saveDraft(), 400);
    };
    form.addEventListener('input', queueSave);
    form.addEventListener('change', queueSave);

    this.querySelectorAll('[data-checkin-draft-clear]').forEach((clearBtn) => {
      clearBtn.addEventListener('click', () => {
        /* Suppress saving while the form empties: form.reset() fires change
           events, and without this the very next debounce would write a fresh
           draft of the blank form and the notice would come back on reload. */
        this.suppressDraft = true;
        this.form.reset();
        this.clearDraft();
        this.syncDeposit();
        this.furthest = 1;
        this.show(1);
        window.setTimeout(() => { this.suppressDraft = false; }, 0);
      });
    });

    this.initDictation();
    this.initFilter();
    this.initStepNav();

    /* Last: initStepNav() renders off this.furthest, which loadDraft() sets, and
       the resumed step has to be shown after the nav exists. */
    this.draftStep = this.loadDraft();

    /* The wizard opens behind an intro card, so the notice inside the form is
       not visible yet. Relabel the entry button instead — otherwise a returning
       customer is invited to "Check in a bike" with no hint that their previous
       answers are waiting behind it. */
    if (this.draftStep) startBtn.textContent = 'Resume your check-in';
  }

  disconnectedCallback() {
    // A section re-render in the theme editor discards this instance. Without
    // this the microphone would keep listening against a detached node, and the
    // window-level pageshow listener would leak one instance per re-render.
    window.removeEventListener('pageshow', this.onPageShow);
    this.filter?.destroy();
    if (this.rec) {
      try { this.rec.stop(); } catch (err) { /* already stopped */ }
    }
  }

  /**
   * Scroll behaviour for anything this wizard moves. A full-page scroll fires
   * on every step change, which is exactly the kind of motion the OS setting
   * exists to stop. Read live rather than cached — the setting can change
   * mid-session.
   */
  motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  show(n) {
    this.current = Math.min(Math.max(n, 1), this.LAST);

    // The high-water mark of steps ever reached. Drives which chips the step
    // nav lets the customer jump back into — never reduced by moving
    // backward, so Back/forward-jump navigation doesn't re-lock ground
    // already covered.
    this.furthest = Math.max(this.furthest || 1, this.current);
    this.renderStepNav();

    this.steps.forEach((s) => {
      s.hidden = Number(s.dataset.step) !== this.current;
    });
    const onReview = this.current === this.LAST;
    this.progress.textContent = onReview ? 'Review' : `Step ${this.current} of ${this.LAST - 1}`;
    this.backBtn.hidden = this.current === 1;
    this.nextBtn.hidden = onReview;
    this.submitBtn.hidden = !onReview;
    if (onReview) this.renderReview();
    // Move focus into the step that just appeared. Without this the button the
    // customer just pressed hides itself, focus falls to <body>, and the next
    // Tab restarts at the top of the document — measured landing on the panel's
    // location link at the exact moment they reach the review. Focusing the
    // fieldset rather than its first input also gets the <legend> announced.
    // preventScroll because the scroll below owns where the page ends up.
    this.steps[this.current - 1].focus({ preventScroll: true });

    // Bring the panel top into view rather than the page top, so the customer
    // does not lose their place on a long step.
    this.scrollIntoView({ block: 'start', behavior: this.motion() });
  }

  /**
   * Validate one step, defaulting to the one in view, where fields are
   * focusable. Reports: on the first invalid field it calls reportValidity()
   * (which needs the field visible to focus it), and it toggles the menu
   * error's `hidden`. Callers that want to look ahead at a step that is not
   * yet shown must use stepHasInvalid() instead — reporting into a hidden
   * fieldset is a no-op at best and, for reportValidity(), can throw.
   */
  stepIsValid(step = this.current) {
    const panel = this.steps[step - 1];
    const fields = panel.querySelectorAll('input, select, textarea');
    for (const el of fields) {
      if (el.disabled || el.type === 'file') continue;
      if (!el.checkValidity()) {
        el.reportValidity();
        return false;
      }
    }

    // The service menu is entirely optional controls, so the loop above can
    // never catch an empty one — and an empty check-in still charges a
    // diagnostic and reaches the mechanic with no requested work. The menu
    // carries "Not sure — diagnose it" for customers who genuinely don't know.
    const menuError = panel.querySelector('[data-checkin-menu-error]');
    if (menuError) {
      const picked = panel.querySelector('[data-service-tier]:checked');
      menuError.hidden = Boolean(picked);
      if (!picked) {
        menuError.scrollIntoView({ block: 'center', behavior: this.motion() });
        return false;
      }
    }

    return true;
  }

  /**
   * Same predicate as stepIsValid(), but silent: no reportValidity(), no
   * menuError.hidden mutation. Used by the step nav's forward jump to look
   * ahead across every step between the current one and the target without
   * flashing through them or clobbering error state the customer hasn't
   * seen yet — reporting only happens once show() has landed on whichever
   * step actually needs it.
   */
  stepHasInvalid(step) {
    const panel = this.steps[step - 1];
    const fields = panel.querySelectorAll('input, select, textarea');
    for (const el of fields) {
      if (el.disabled || el.type === 'file') continue;
      if (!el.checkValidity()) return true;
    }

    const menuError = panel.querySelector('[data-checkin-menu-error]');
    if (menuError) {
      const picked = panel.querySelector('[data-service-tier]:checked');
      if (!picked) return true;
    }

    return false;
  }

  /**
   * Deposit follows the menu: any tune-up ticked means the $25 diagnostic,
   * otherwise the $10. Mirrors the fee band — "$25 for any tune-up, $10 for
   * à la carte work only".
   */
  syncDeposit() {
    const ticked = [...this.form.querySelectorAll('[data-service-tier]:checked')];
    const isTuneUp = ticked.some((el) => el.dataset.serviceTier === 'tuneup');
    this.variantInput.value = isTuneUp
      ? this.variantInput.dataset.variantTuneup
      : this.variantInput.dataset.variantAlacarte;
    return isTuneUp;
  }

  renderReview() {
    const isTuneUp = this.syncDeposit();
    const rows = [];
    const services = [];
    // Where the menu sits in DOM order, so the grouped row lands between the
    // bike details and the drop-off question rather than at the end.
    let servicesAt = -1;

    this.form.querySelectorAll('[name^="properties["]').forEach((el) => {
      const label = el.name.slice('properties['.length, -1);

      // Every menu row is its own line item property, which read back as-is
      // gave one "Yes" per ticked service against a column of shouty labels.
      // The customer approves a charge from this screen; it should read like a
      // summary. What gets posted is unchanged — this is display only.
      if (el.dataset.serviceTier) {
        if (servicesAt < 0) servicesAt = rows.length;
        if (el.checked) services.push(label);
        return;
      }

      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) rows.push([label, el.value]);
      } else if (el.type === 'file') {
        if (el.files && el.files.length) rows.push([label, el.files[0].name]);
      } else if (el.value.trim()) {
        rows.push([label, el.value.trim()]);
      }
    });

    if (services.length) {
      rows.splice(servicesAt < 0 ? rows.length : servicesAt, 0,
                  ['Services', services.join(' · ')]);
    }

    this.reviewList.innerHTML = '';
    rows.forEach(([k, v]) => {
      const dt = document.createElement('dt');
      dt.textContent = k;
      const dd = document.createElement('dd');
      dd.textContent = v;
      this.reviewList.append(dt, dd);
    });

    this.dueLine.textContent = isTuneUp
      ? 'Due today: the tune-up diagnostic, credited toward the work.'
      : 'Due today: the à la carte diagnostic, credited toward the work.';
  }

  /**
   * Drop empty optional fields on submit. Shopify stores a line item property
   * even when its value is an empty string, which would pad the mechanic's work
   * order with blank rows. Disabled controls are not submitted.
   *
   * What gets disabled is recorded so restore() can undo exactly this and
   * nothing else — a field the customer disabled some other way stays disabled.
   */
  dropEmptyProperties() {
    this.disabledFields = [];
    this.form.querySelectorAll('[name^="properties["]').forEach((el) => {
      if (el.type === 'checkbox' || el.type === 'radio' || el.disabled) return;
      const empty = el.type === 'file'
        ? !(el.files && el.files.length)
        : el.value.trim() === '';
      if (empty) {
        el.disabled = true;
        this.disabledFields.push(el);
      }
    });
  }

  /** Undo everything the submit path did, for a page restored from bfcache. */
  restore() {
    this.disabledFields.forEach((el) => { el.disabled = false; });
    this.disabledFields = [];
    this.submitting = false;
    this.submitBtn.disabled = false;
    this.submitBtn.textContent = this.submitLabel;
  }

  /* ---- draft persistence -------------------------------------------------
     Distinct from restore() above, which undoes the submit path's field
     disabling after a bfcache restore. These three deal with localStorage and
     are named apart on purpose — the two have nothing to do with each other.  */

  /** Serialise the form. File inputs are skipped: a browser will not let a page
      set the value of a file input, so photos can never be restored — the
      notice tells the customer to attach them again instead. */
  serialiseDraft() {
    const fields = {};
    let hadPhotos = false;

    this.form.querySelectorAll('[name]').forEach((el) => {
      if (!el.name.startsWith('properties[')) return;
      if (el.type === 'file') {
        if (el.files && el.files.length) hadPhotos = true;
        return;
      }
      if (el.type === 'checkbox') {
        if (el.checked) fields[el.name] = el.value;
        return;
      }
      if (el.type === 'radio') {
        if (el.checked) fields[el.name] = el.value;
        return;
      }
      if (el.value !== '') fields[el.name] = el.value;
    });

    return {
      savedAt: Date.now(),
      step: this.current,
      furthest: this.furthest || 1,
      hadPhotos,
      fields,
    };
  }

  saveDraft() {
    if (this.submitting || this.suppressDraft) return;
    const draft = this.serialiseDraft();
    /* Nothing typed yet — do not leave an empty draft behind that would then
       show a "we kept your check-in" notice offering the customer nothing. */
    if (!Object.keys(draft.fields).length && !draft.hadPhotos) {
      draftStore.clear(this.draftKey);
      return;
    }
    draftStore.write(this.draftKey, draft);
  }

  /** Fade the status line out after DRAFT_NOTICE_MS. The whole notice goes,
      "Start fresh" included — see the note in the Liquid about the persistent
      control that replaces it. */
  hideDraftNotice(notice) {
    window.clearTimeout(this.draftNoticeTimer);
    this.draftNoticeTimer = window.setTimeout(() => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { notice.hidden = true; return; }
      notice.classList.add('is-leaving');
      window.setTimeout(() => {
        notice.hidden = true;
        notice.classList.remove('is-leaving');
      }, DRAFT_NOTICE_FADE_MS);
    }, DRAFT_NOTICE_MS);
  }

  clearDraft() {
    draftStore.clear(this.draftKey);
    window.clearTimeout(this.draftNoticeTimer);
    const notice = this.querySelector('[data-checkin-draft]');
    if (notice) {
      notice.hidden = true;
      notice.classList.remove('is-leaving');
    }
    const reset = this.querySelector('[data-checkin-draft-reset]');
    if (reset) reset.hidden = true;
  }

  /** Apply a stored draft, if there is one, and say so. Returns the step to
      resume on, or null when nothing was restored. */
  loadDraft() {
    const draft = draftStore.read(this.draftKey);
    if (!draft || !draft.fields) return null;

    let applied = 0;
    for (const [name, value] of Object.entries(draft.fields)) {
      const controls = this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`);
      controls.forEach((el) => {
        if (el.type === 'file') return;
        if (el.type === 'checkbox' || el.type === 'radio') {
          if (el.value === value) { el.checked = true; applied += 1; }
        } else {
          el.value = value;
          applied += 1;
        }
      });
    }
    if (!applied) return null;

    /* The deposit tier is derived from the service checkboxes, so it has to be
       recomputed after they are re-checked or the review would quote the wrong
       diagnostic against a restored selection. */
    this.syncDeposit();

    const notice = this.querySelector('[data-checkin-draft]');
    const text = this.querySelector('[data-checkin-draft-text]');
    if (notice && text) {
      text.textContent = draft.hadPhotos
        ? `We kept your check-in from ${describeAge(draft.savedAt)}. Your photos need attaching again — a browser won’t let us hold on to those.`
        : `We kept your check-in from ${describeAge(draft.savedAt)}.`;
      notice.hidden = false;

      /* Transient when it is purely a status line. NOT when photos were
         dropped: that sentence is a task the customer still has to do, on the
         very step the file inputs live, and timing it out would lose the only
         mention they get that their photos are gone.

         The countdown is NOT started here. loadDraft() runs at page load, while
         the customer is still on the intro card with the form hidden — starting
         it here spent most of the eight seconds before the notice was on screen
         at all, and measured as the notice vanishing ~5.6s after the form
         opened. The start button begins it instead, when the notice is first
         actually visible. */
      this.draftNoticeTransient = !draft.hadPhotos;
    }

    const reset = this.querySelector('[data-checkin-draft-reset]');
    if (reset) reset.hidden = false;

    this.furthest = Math.max(draft.furthest || 1, 1);
    return Math.min(Math.max(draft.step || 1, 1), this.LAST);
  }

  initDictation() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const dictate = this.form.querySelector('[data-checkin-dictate]');
    const textarea = this.form.querySelector('#rf-symptoms');
    if (!Recognition || !dictate || !textarea) return;

    // Only revealed once the API is confirmed present — a dictation button that
    // does nothing is worse than no button at all.
    dictate.hidden = false;

    const btn = dictate.querySelector('[data-checkin-dictate-btn]');
    const status = dictate.querySelector('[data-checkin-dictate-status]');
    const rec = new Recognition();
    this.rec = rec;
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = document.documentElement.lang || 'en-US';

    let listening = false;

    const setState = (on, message) => {
      listening = on;
      btn.setAttribute('aria-pressed', String(on));
      btn.textContent = on ? 'Stop listening' : 'Tap & talk instead';
      status.textContent = message || '';
    };

    rec.addEventListener('result', (event) => {
      let said = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        said += event.results[i][0].transcript;
      }
      said = said.trim();
      if (!said) return;
      textarea.value = textarea.value.trim()
        ? `${textarea.value.trim()} ${said}`
        : said;
    });

    rec.addEventListener('error', (event) => {
      setState(false, event.error === 'not-allowed'
        ? 'Microphone blocked — type it instead.'
        : 'Didn’t catch that — type it instead.');
    });

    rec.addEventListener('end', () => { if (listening) setState(false, ''); });

    btn.addEventListener('click', () => {
      if (listening) { rec.stop(); setState(false, ''); return; }
      try {
        rec.start();
        setState(true, 'Listening — talk it through.');
      } catch (err) {
        setState(false, 'Could not start the microphone.');
      }
    });
  }

  /**
   * Quick filter over the 39-row service menu. Ships `hidden` in the markup
   * and is only revealed at the end of this method — a filter that does
   * nothing without JS is worse than none.
   */
  initFilter() {
    const menu = this.querySelector('.rf-checkin__menu');
    // The step this menu lives on, derived rather than hardcoded — the wizard
    // has been renumbered once already and will be again.
    const stepEl = menu ? menu.closest('[data-step]') : null;
    const filterStep = stepEl ? Number(stepEl.dataset.step) : null;

    // "/" must not steal focus while another step is showing.
    this.filter = initServiceFilter(this, {
      isActive: () => filterStep !== null && this.current === filterStep,
    });
  }

  /**
   * Step-jump chips under the progress line. One button per fieldset, built
   * once here and re-rendered on every show() so their enabled/current state
   * always matches where the customer actually is. Ships `hidden` in the
   * markup and is only revealed once populated — see the snippet comment for
   * why it stays out of the <noscript> fallback: every affordance here is
   * script-driven, and a step-jump control that does nothing without JS is
   * worse than none.
   */
  initStepNav() {
    const nav = this.querySelector('[data-checkin-steps]');
    if (!nav) return;

    const list = document.createElement('ol');
    list.className = 'rf-checkin__steps-list';

    this.stepNavButtons = this.steps.map((panel, i) => {
      const step = i + 1;
      const li = document.createElement('li');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rf-checkin__step-chip';
      btn.dataset.checkinStepJump = String(step);

      const num = document.createElement('span');
      num.className = 'rf-checkin__step-chip-num';
      num.textContent = String(step);

      const label = document.createElement('span');
      label.className = 'rf-checkin__step-chip-label';
      label.textContent = panel.dataset.stepLabel || '';

      btn.append(num, label);
      btn.addEventListener('click', () => this.jumpToStep(step));

      li.append(btn);
      list.append(li);
      return btn;
    });

    nav.append(list);
    nav.hidden = false;
    this.renderStepNav();
  }

  /** Re-derive every chip's enabled/current/label state from this.current
   * and this.furthest. Called from show() so the nav never drifts from what
   * the wizard is actually doing. */
  renderStepNav() {
    if (!this.stepNavButtons) return;
    const furthest = this.furthest || 1;

    this.stepNavButtons.forEach((btn, i) => {
      const step = i + 1;
      const label = this.steps[i].dataset.stepLabel || '';
      const isCurrent = step === this.current;
      const reachable = step <= furthest;

      btn.disabled = !reachable;
      btn.classList.toggle('rf-checkin__step-chip--current', isCurrent);

      if (isCurrent) {
        btn.setAttribute('aria-current', 'step');
      } else {
        btn.removeAttribute('aria-current');
      }

      let state = 'not yet available';
      if (isCurrent) state = 'current';
      else if (reachable) state = 'completed';
      btn.setAttribute('aria-label', `Step ${step}, ${label}, ${state}`);
    });
  }

  /**
   * Handle a click on a step chip. Backward moves are free — nothing to
   * lose by looking at a step already passed. Forward moves must validate
   * every step between here and the target, EVEN steps already reached:
   * the furthest watermark only proves a step was valid when the customer
   * left it, not that it still is. Without re-checking, clearing a required
   * field on step 1, advancing, then jumping straight to Review would reach
   * checkout with the field empty — the same defect class as the Enter-key
   * submit bug this wizard already had to fix.
   *
   * Looks ahead with the silent stepHasInvalid() rather than stepIsValid()
   * so the customer never sees the wizard flash through intermediate steps
   * before landing on the one that actually needs attention. Only once
   * show() has landed there does stepIsValid() run for real, because
   * reportValidity() cannot focus a field inside a fieldset that is still
   * hidden.
   */
  jumpToStep(target) {
    if (target < this.current) {
      this.show(target);
      return;
    }

    for (let s = this.current; s < target; s += 1) {
      if (this.stepHasInvalid(s)) {
        this.show(s);
        this.stepIsValid();
        return;
      }
    }

    this.show(target);
  }
}

customElements.define('rifugio-service-checkin', RifugioServiceCheckin);
